/** Leituras compartilhadas (React Query): cadastros e projeto, com cache e atualização após salvar. */
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { api, errorMessage } from './api';
import type { Lookups, Metrics, ProjectDetail } from './types';
import { useToast } from '../components/pmis';

export function useLookups() {
  return useQuery({ queryKey: ['lookups'], queryFn: () => api<Lookups>('/api/v1/lookups'), staleTime: 60_000 });
}

type LookupUser = Lookups['users'][number];
/**
 * Pessoas com o perfil (ex.: GERENTE, ENGENHEIRO, VENDEDOR, ARQUITETO; veja PROJECT_ROLE_PROFILE): é quem aparece nos
 * campos e filtros daquele papel. keep: a pessoa já escolhida continua na lista mesmo sem o perfil (para não sumir do
 * projeto ao editar). active: só os ativos (padrão).
 */
export function withProfile(users: LookupUser[] | undefined, profile: string | null, opts: { keep?: number | string | null; active?: boolean } = {}): LookupUser[] {
  const keep = opts.keep == null || opts.keep === '' ? null : Number(opts.keep);
  return (users ?? []).filter((u) => u.id === keep || ((opts.active === false || u.status === 'active') && (!profile || u.roles.includes(profile))));
}

export function useProject(id: number) {
  return useQuery({ queryKey: ['project', id], queryFn: () => api<ProjectDetail>(`/api/v1/projects/${id}`) });
}

export function useMetrics(id: number) {
  return useQuery({ queryKey: ['project', id, 'metrics'], queryFn: () => api<Metrics>(`/api/v1/projects/${id}/metrics`) });
}

/** Mutação com aviso de sucesso/erro e recarga das chaves afetadas. */
export function useSave<TBody, TOut = unknown>(fn: (body: TBody) => Promise<TOut>, opts: { invalidate?: QueryKey[]; ok?: string | ((out: TOut) => string); onDone?: (out: TOut) => void } = {}) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: async (out) => {
      for (const k of opts.invalidate ?? []) await qc.invalidateQueries({ queryKey: k });
      if (opts.ok) toast(typeof opts.ok === 'function' ? opts.ok(out) : opts.ok, 'ok');
      opts.onDone?.(out);
    },
    onError: (e) => toast(errorMessage(e), 'err'),
  });
}
